import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Percent from "_shared/percentages/percent";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import Proposal from "work/proposals/proposal";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";
import {
  Masked,
  MaskedTeam,
  MaskedFeeSchedule,
  MaskedContracts,
  MaskedDiscount,
  FeeScheduleCategoryMaskedCurrency,
} from "work/masked-fields";

export default class ProposalIssues {
  public static fromProposal(proposal: Proposal): ProposalIssues {
    const issues = new ProposalIssues();
    issues.validateClientTeam(proposal.workAgreement.clientTeam, proposal.workAgreement.vendorTeam);
    issues.validateName(proposal.name);
    issues.validateDescription(proposal.description);
    issues.validateDates(proposal.responseDueBy, proposal.startDate, proposal.endDate);
    issues.validateVendorTeam(proposal?.vendorTeam);
    issues.validateFeeSchedule({
      feeSchedule: proposal.feeSchedule ?? [],
      negotiable: proposal.negotiable.value,
      vendorTeam: proposal.vendorTeam,
    });
    issues.validateConflicts({
      conflictsCheckWaived: proposal.conflictsCheckWaived ?? new AHBoolean(false),
      documents: proposal.conflicts ?? [],
      negotiable: proposal.negotiable.value,
    });
    issues.generateKey();
    return issues;
  }

  public static fromBuilder(builder?: ProposalBuilder): ProposalIssues {
    const issues = new ProposalIssues();
    if (!builder) {
      return issues;
    }

    issues.validateClientTeam(
      builder.currentSpec.workAgreement.clientTeam,
      builder.currentSpec.workAgreement.vendorTeam,
    );
    issues.validateName(builder.currentSpec.workAgreement.name);
    issues.validateDescription(builder.currentSpec.workAgreement.description);
    issues.validateDates(
      builder.currentSpec.responseDueBy,
      builder.currentSpec.workAgreement.startDate,
      builder.currentSpec.workAgreement.endDate,
    );
    issues.validateVendorTeam(builder.currentSpec.workAgreement.vendorTeam);

    if (Masked.isNotMasked(builder.currentSpec.workAgreement.feeSchedule)) {
      issues.validateFeeSchedule({
        feeSchedule: builder.currentSpec.workAgreement.feeSchedule ?? [],
        negotiable: builder.currentSpec.negotiable.value,
        vendorTeam: builder.currentSpec.workAgreement.vendorTeam,
      });
    }

    issues.validateConflicts({
      conflictsCheckWaived: builder.currentSpec.workAgreement.conflictsCheckWaived ?? new AHBoolean(false),
      documents: builder.currentSpec.workAgreement.conflicts ?? [],
      negotiable: builder.currentSpec.negotiable.value,
    });
    issues.generateKey();
    return issues;
  }

  public static fromRedline(redline: ProposalRedline): ProposalIssues {
    const issues = new ProposalIssues();

    issues.validateNameRedline(redline.name);
    issues.validateDescriptionRedline(redline.description);
    issues.validateDatesRedline(redline.responseDueBy, redline.startDate, redline.endDate);
    issues.validateTeamRedline(redline.teamRestricted, redline.vendorTeam);
    issues.validateFeeScheduleRedline(redline.feeSchedule);
    issues.validateConflictsRedline(redline.conflictsCheckWaived, redline.conflicts);
    issues.validatePolicyDocumentsRedline(redline.clientPolicies);
    issues.validatePolicyDocumentsRedline(redline.vendorPolicies);
    issues.validateDiscountRedline(redline.discount);
    issues.generateKey();
    return issues;
  }

  public get entries(): ProposalIssue[] {
    return [...this._entries];
  }

  public get canSubmit(): boolean {
    return this.canSave && !this._entries.some((entry) => entry.field.name === ProposalFieldName.TeamLeader);
  }

  public get canSave(): boolean {
    return !this._entries
      .filter((entry) => entry.field.name !== ProposalFieldName.TeamLeader)
      .some((entry) => entry.level === ProposalIssueLevel.Critical);
  }
  public get key(): string | undefined {
    return this._key;
  }

  private _key?: string;
  private _entries: ProposalIssue[] = [];

  private validateClientTeam(
    clientTeam?: ClientTeam | MaskedTeam | null,
    vendorTeam?: VendorTeam | MaskedTeam | null,
  ): void {
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Client);
    if (Masked.isMasked(clientTeam)) return;
    if (!clientTeam?.leader) {
      this._entries.push(
        new ProposalIssue(ProposalField.Client, "Client is a required field.", ProposalIssueLevel.Critical),
      );
      return;
    }

    if (Masked.isMasked(vendorTeam)) return;
    if (clientTeam.leader.isEqualTo(vendorTeam?.leader)) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Client,
          "Client cannot be the same as vendor-team leader.",
          ProposalIssueLevel.Critical,
        ),
        new ProposalIssue(
          ProposalField.TeamLeader(clientTeam.leader.userId),
          "Client cannot be the same as vendor-team leader.",
          ProposalIssueLevel.Critical,
        ),
      );
    }
  }

  private generateKey() {
    this._key = this._entries
      .toSorted((a, b) => a.field.name.localeCompare(b.field.name))
      .map((entry) => `${entry.field.name}-${entry.description}-${entry.level}`)
      .join("|");
  }

  private validateName(name?: ProjectName | null): void {
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Name);
    if (!name || name.value.trim() === "") {
      this._entries.push(
        new ProposalIssue(ProposalField.Name, "Name is a required field.", ProposalIssueLevel.Critical),
      );
    }
  }

  private validateNameRedline(nameRedline: FieldRedline<ProjectName>): void {
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Name);

    if (nameRedline.isResolved) {
      this.validateName(nameRedline.currentEntry);
    } else {
      this._entries.push(
        new ProposalIssue(ProposalField.Name, "Changes requested to name.", ProposalIssueLevel.Critical),
      );
    }
  }

  private validateDescription(description?: ProjectDescription | null): void {
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Description);
    if (!description || description.value.trim() === "") {
      this._entries.push(
        new ProposalIssue(ProposalField.Description, "Description is a required field.", ProposalIssueLevel.Critical),
      );
    }
  }

  private validateDescriptionRedline(descriptionRedline: FieldRedline<ProjectDescription>): void {
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Description);

    if (descriptionRedline.isResolved) {
      this.validateDescription(descriptionRedline.currentEntry);
    } else {
      this._entries.push(
        new ProposalIssue(ProposalField.Description, "Changes requested to description.", ProposalIssueLevel.Critical),
      );
    }
  }

  private validateDates(responseDueBy?: AHDate | null, startDate?: AHDate | null, endDate?: AHDate | null): void {
    const dateFieldNames = new Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]>([
      ProposalFieldName.ResponseDueBy,
      ProposalFieldName.StartDate,
      ProposalFieldName.EndDate,
    ]);
    this._entries = this._entries.filter((entry) => !dateFieldNames.has(entry.field.name));
    const now = new AHDate();

    if (responseDueBy && !responseDueBy.isValid) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Response due by date is an invalid date.",
          ProposalIssueLevel.Critical,
        ),
      );
    }
    if (responseDueBy && responseDueBy.isValid && now.isAfter(responseDueBy, "day")) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Response due by cannot be in the past.",
          ProposalIssueLevel.Critical,
        ),
      );
    }

    if (startDate && !startDate.isValid) {
      this._entries.push(
        new ProposalIssue(ProposalField.StartDate, "Start date is an invalid date.", ProposalIssueLevel.Critical),
      );
    }
    if (startDate && startDate.isValid && now.isAfter(startDate, "day")) {
      this._entries.push(
        new ProposalIssue(ProposalField.StartDate, "Start date cannot be in the past.", ProposalIssueLevel.Critical),
      );
    }

    if (endDate && !endDate.isValid) {
      this._entries.push(
        new ProposalIssue(ProposalField.EndDate, "End date is an invalid date.", ProposalIssueLevel.Critical),
      );
    }
    if (endDate && endDate.isValid && now.isAfter(endDate, "day")) {
      this._entries.push(
        new ProposalIssue(ProposalField.EndDate, "End date cannot be in the past.", ProposalIssueLevel.Critical),
      );
    }
    if (responseDueBy?.isValid && startDate?.isValid && responseDueBy.isAfter(startDate, "day")) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Response due by date cannot be after start date.",
          ProposalIssueLevel.Critical,
        ),
      );
    }
    if (startDate?.isValid && endDate?.isValid && startDate.isAfter(endDate, "day")) {
      this._entries.push(
        new ProposalIssue(ProposalField.StartDate, "Start date cannot be after end date.", ProposalIssueLevel.Critical),
      );
    }
  }

  private validateDatesRedline(
    responseDueByRedline: FieldRedline<AHDate>,
    startDateRedline: FieldRedline<AHDate>,
    endDateRedline: FieldRedline<AHDate>,
  ): void {
    const dateFieldNames = new Set<(typeof ProposalFieldName)[keyof typeof ProposalFieldName]>([
      ProposalFieldName.ResponseDueBy,
      ProposalFieldName.StartDate,
      ProposalFieldName.EndDate,
    ]);
    this._entries = this._entries.filter((entry) => !dateFieldNames.has(entry.field.name));

    if (!responseDueByRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.ResponseDueBy,
          "Changes requested to response due by date.",
          ProposalIssueLevel.Critical,
        ),
      );
    }

    if (!startDateRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(ProposalField.StartDate, "Changes requested to start date.", ProposalIssueLevel.Critical),
      );
    }

    if (!endDateRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(ProposalField.EndDate, "Changes requested to end date.", ProposalIssueLevel.Critical),
      );
    }

    if (responseDueByRedline.isResolved && startDateRedline.isResolved && endDateRedline.isResolved) {
      this.validateDates(responseDueByRedline.currentEntry, startDateRedline.currentEntry, endDateRedline.currentEntry);
    }
  }

  private validateVendorTeam(team?: VendorTeam | MaskedTeam | null): void {
    if (Masked.isMasked(team)) return;

    this._entries = this._entries.filter((entry) => entry.field.category !== ProposalFieldCategory.Team);
    if (!team?.leader) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.TeamLeader(team?.leader?.userId),
          "Team leader is required in order to submit proposal.",
          ProposalIssueLevel.Critical,
        ),
      );
    }
  }

  private validateTeamRedline(
    teamRestrictedRedline: FieldRedline<AHBoolean> | MaskedTeam,
    teamRedline: FieldRedlineArray<EntityMember> | MaskedTeam,
  ): void {
    if (Masked.isMasked(teamRestrictedRedline)) return;
    if (Masked.isMasked(teamRedline)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Team);

    if (!teamRestrictedRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.TeamRestriction,
          "Changes requested to vendor-team restriction.",
          ProposalIssueLevel.Critical,
        ),
      );
    }

    for (const memberRedline of teamRedline.redlines) {
      if (!memberRedline.isResolved) {
        this._entries.push(
          new ProposalIssue(memberRedline.field, "Changes requested for team member.", ProposalIssueLevel.Critical),
        );
      }
    }
  }

  private validateFeeSchedule(kwargs: {
    feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule;
    negotiable?: boolean;
    vendorTeam?: VendorTeam | MaskedTeam | null;
  }): void {
    const { feeSchedule, negotiable = false, vendorTeam } = kwargs;
    if (Masked.isMasked(feeSchedule)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.FeeSchedule);
    if (feeSchedule.length === 0) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeSchedule,
          "Fee schedule is required in order to hire proposal.",
          negotiable ? ProposalIssueLevel.Warning : ProposalIssueLevel.Critical,
        ),
      );
      return;
    }
    for (const deferredCategory of feeSchedule.filter(
      (category) => category instanceof FeeScheduleCategory && !category.fee,
    )) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeScheduleCategory(deferredCategory.id),
          `Deferred fee for ${deferredCategory.name} must be set to a valid
           amount before the proposal can be hired.`,
          ProposalIssueLevel.Warning,
        ),
      );
    }
    for (const hourlyCategory of feeSchedule.filter(
      (category): category is FeeScheduleCategory =>
        category instanceof FeeScheduleCategory && category.isHourly && !category.teamMemberIsMasked,
    )) {
      if (teamMemberBelongsToVendorTeam(hourlyCategory.teamMember, vendorTeam)) continue;
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeScheduleCategory(hourlyCategory.id),
          `Hourly rate for ${hourlyCategory.name} must be associated with a vendor team member before the proposal can be hired.`,
          negotiable ? ProposalIssueLevel.Warning : ProposalIssueLevel.Critical,
        ),
      );
    }
  }

  private validateFeeScheduleRedline(
    feeScheduleRedline: FeeScheduleRedline | MaskedFeeSchedule,
  ): void {
    if (Masked.isMasked(feeScheduleRedline)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.FeeSchedule);

    for (const categoryRedline of feeScheduleRedline.redlines) {
      if (!categoryRedline.isResolved) {
        this._entries.push(
          new ProposalIssue(
            categoryRedline.field,
            `Changes requested to fee schedule category: ${
              categoryRedline.revisedEntry?.name ?? categoryRedline.originalEntry?.name
            }.`,
            ProposalIssueLevel.Critical,
          ),
        );
      } else if (
        categoryRedline.currentEntry instanceof FeeScheduleCategory &&
        categoryRedline.currentEntry &&
        !categoryRedline.currentEntry.fee
      ) {
        this._entries.push(
          new ProposalIssue(
            categoryRedline.field,
            `Deferred fee for ${categoryRedline.currentEntry.name} must be set to a valid
             amount before the proposal can be approved or hired.`,
            ProposalIssueLevel.Warning,
          ),
        );
      } else if (
        categoryRedline.currentEntry instanceof FeeScheduleCategory &&
        categoryRedline.currentEntry.isHourly &&
        !categoryRedline.currentEntry.teamMemberIsMasked &&
        !categoryRedline.currentEntry.teamMember
      ) {
        this._entries.push(
          new ProposalIssue(
            categoryRedline.field,
            `Hourly rate for ${categoryRedline.currentEntry.name} must be associated with a vendor team member before the proposal can be approved or hired.`,
            ProposalIssueLevel.Warning,
          ),
        );
      }
    }
    if (
      feeScheduleRedline.redlines.length < 1 ||
      (feeScheduleRedline.isResolved &&
        feeScheduleRedline.redlines.filter((redline) => redline.currentEntry).length < 1)
    ) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.FeeSchedule,
          "Fee schedule is required before the proposal can be approved or hired.",
          ProposalIssueLevel.Warning,
        ),
      );
    }
  }

  private validateConflicts(kwargs: {
    conflictsCheckWaived: AHBoolean | MaskedContracts;
    documents: WorkDocument[] | MaskedContracts;
    negotiable: boolean;
  }) {
    const { conflictsCheckWaived, documents, negotiable } = kwargs;
    if (Masked.isMasked(conflictsCheckWaived)) return;
    if (Masked.isMasked(documents)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Conflicts);
    if (!conflictsCheckWaived.value && documents.length === 0) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Conflicts,
          "Conflicts document(s) are required in order to hire when conflicts check is not waived.",
          negotiable ? ProposalIssueLevel.Warning : ProposalIssueLevel.Critical,
        ),
      );
    }
  }

  private validateConflictsRedline(
    conflictsCheckWaived: FieldRedline<AHBoolean> | MaskedContracts,
    documentsRedline: FieldRedlineArray<WorkDocument> | MaskedContracts,
  ) {
    if (Masked.isMasked(conflictsCheckWaived)) return;
    if (Masked.isMasked(documentsRedline)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Conflicts);

    if (!conflictsCheckWaived.isResolved) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.WaiveConflictsCheck,
          "Changes requested to conflicts check waiver.",
          ProposalIssueLevel.Critical,
        ),
      );
    }

    for (const documentRedline of documentsRedline.redlines) {
      if (!documentRedline.isResolved) {
        this._entries.push(
          new ProposalIssue(
            documentRedline.field,
            "Changes requested to conflicts document(s).",
            ProposalIssueLevel.Critical,
          ),
        );
      }
    }

    if (
      conflictsCheckWaived.isResolved &&
      !conflictsCheckWaived.currentEntry?.value &&
      documentsRedline.redlines.filter((redline) => redline.currentEntry).length < 1
    ) {
      this._entries.push(
        new ProposalIssue(
          ProposalField.Conflicts,
          "Conflicts document(s) are required before the proposal can be approved or hired, unless the conflicts check is waived.",
          ProposalIssueLevel.Warning,
        ),
      );
    }
  }

  private validatePolicyDocumentsRedline(documentsRedline: FieldRedlineArray<WorkDocument> | MaskedContracts) {
    if (Masked.isMasked(documentsRedline)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== documentsRedline.field.name);

    for (const documentRedline of documentsRedline.redlines) {
      if (!documentRedline.isResolved && documentRedline.field.id) {
        this._entries.push(
          new ProposalIssue(
            documentRedline.field,
            "Changes requested to policy document.",
            ProposalIssueLevel.Critical,
          ),
        );
      }
    }
  }

  private validateDiscountRedline(discountRedline: FieldRedline<Percent> | MaskedDiscount): void {
    if (Masked.isMasked(discountRedline)) return;
    this._entries = this._entries.filter((entry) => entry.field.name !== ProposalFieldName.Discount);

    if (!discountRedline.isResolved) {
      this._entries.push(
        new ProposalIssue(ProposalField.Discount, "Changes requested to discount.", ProposalIssueLevel.Critical),
      );
    }
  }
}

function teamMemberBelongsToVendorTeam(
  member: EntityMember | null | undefined,
  vendorTeam: VendorTeam | MaskedTeam | null | undefined,
): boolean {
  if (!member || !Masked.isPresentNotMasked(vendorTeam)) return false;
  return Boolean(
    vendorTeam.leader?.isEqualTo(member) || vendorTeam.members.some((candidate) => candidate.isEqualTo(member)),
  );
}

export class ProposalIssue {
  public readonly field: ProposalField;
  public readonly description: string;
  public readonly level: ProposalIssueLevel;

  constructor(field: ProposalField, description: string, level: ProposalIssueLevel) {
    this.field = field;
    this.description = description;
    this.level = level;
  }
}

export enum ProposalIssueLevel {
  Critical,
  Warning,
  Info,
}
